import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import AppContext from '../../contexts/AppContext';
import ThemeContext from '../../contexts/ThemeContext';
import FunctionsContext from '../../contexts/FunctionsContext';
import Label from '../Label';
import ChipGroup from '../ChipGroup';
import UsergroupSelect from '../UsergroupSelect';
import { getUsergroups } from '../../services/usergroupService';
import { Section, ErrorText } from '../PrebookingSharedComponents';
import { getUsergroupReferenceDefaultFields } from '../../utils/usergroupReferenceDefaults';
import useUsergroupReferenceDefaults from '../../hooks/useUsergroupReferenceDefaults';

import i18n from '../../i18n';

const SearchContainer = styled.div`
  margin-top: 8px;
`;

const CostAllocation = ({
  formData,
  updateField,
  isCostAllocationMandatory,
  error,
  clearError,
}) => {
  const { colors } = useContext(ThemeContext);
  const { sessionExpired } = useContext(FunctionsContext);
  const { user } = useContext(AppContext);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSearchAllowed, setIsSearchAllowed] = useState(false);

  const costAllocationContainerRef = useRef(null);
  const searchContainerRef = useRef(null);

  // Raw search results, kept so a cost center picked through the "other" search can be
  // looked back up in full. `UsergroupSelect#onChange` hands back only
  // `{ id, name, customFields }` and is shared with the legacy booking flow, so its shape
  // cannot be widened here (TUL-5597).
  const searchedGroupsRef = useRef(new Map());

  // Prefill the reference inputs from the selected cost center (TUL-5597). The translation
  // order wizard only ever creates, so there is no stored value that could outrank a default.
  const referenceDefaultFields = useMemo(
    () => getUsergroupReferenceDefaultFields({ externalOrderIdUsed: user?.userGroup?.externalOrderIdUsed }),
    [user]
  );
  const applyReferenceDefaults = useUsergroupReferenceDefaults({
    updateField,
    fields: referenceDefaultFields,
  });

  useEffect(() => {
    let isMounted = true;
    const fetchUserGroups = async () => {
      setLoading(true);
      try {
        const response = await getUsergroups(null, true);
        if (!isMounted) return;
        const groups = Array.isArray(response) ? response : response.groups || [];
        setUserGroups(groups);
        if (!Array.isArray(response) && response.options) {
          setIsSearchAllowed(!!response.options.allowSearch);
        }
      } catch (e) {
        if (!isMounted) return;
        console.error('Failed to fetch user groups:', e);
        if (e.message === 'Session expired') {
          sessionExpired();
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserGroups();
    return () => { isMounted = false; };
  }, [sessionExpired, updateField]);

  // Auto-select single group when loaded
  useEffect(() => {
    if (!loading && userGroups.length === 1 && !formData.costAllocation) {
      const singleGroup = userGroups[0];
      updateField('costAllocation', singleGroup.id);
      updateField('costAllocationGroup', {
        id: singleGroup.id,
        name: singleGroup.publicName || singleGroup.name,
        invoiceRecipientRef: singleGroup.invoiceRecipientRef,
        invoiceRefDefault: singleGroup.invoiceRefDefault,
        externalOrderIdDefault: singleGroup.externalOrderIdDefault,
      });
      applyReferenceDefaults({
        group: singleGroup,
        previousGroup: formData.costAllocationGroup,
        currentValues: {
          additionalInvoicingInfo: formData.additionalInvoicingInfo,
          externalOrderId: formData.externalOrderId,
        },
      });
    }
    // The reference values are listed only because exhaustive-deps requires it: the effect
    // bails out as soon as `costAllocation` is set, so it cannot loop on its own prefill.
  }, [userGroups, loading, formData.costAllocation, formData.costAllocationGroup,
    formData.additionalInvoicingInfo, formData.externalOrderId, updateField,
    applyReferenceDefaults]);

  const searchUserGroups = async (query) => {
    try {
      const response = await getUsergroups(query);
      const groups = Array.isArray(response) ? response : response.groups || [];
      groups.forEach((group) => {
        if (group.id) { searchedGroupsRef.current.set(group.id, group); }
      });
      return groups;
    } catch (e) {
      if (e.message === 'Session expired') { sessionExpired(); }
      return [];
    }
  };

  const costAllocationOptions = [
    ...userGroups.map(group => ({
      value: group.id,
      label: `${group.name}${group.invoiceRecipientRef ? ` (${group.invoiceRecipientRef})` : ''}`,
    })),
    ...(isSearchAllowed ? [{ value: 'other', label: i18n('other') }] : []),
  ];

  // Auto-detect edit flow: if costAllocation doesn't match any loaded group and search is allowed,
  // switch to 'other' to show search with pre-populated value
  useEffect(() => {
    if (loading || !isSearchAllowed) return;
    if (!formData.costAllocation || formData.costAllocation === 'other') return;
    const matchesLoadedGroup = userGroups.some(g => g.id === formData.costAllocation);
    if (!matchesLoadedGroup && formData.costAllocationGroup?.id) {
      updateField('costAllocation', 'other');
    }
  }, [loading, isSearchAllowed, userGroups, formData.costAllocation, formData.costAllocationGroup, updateField]);

  return (
    <Section ref={costAllocationContainerRef}>
      <Label text={i18n('cost_allocation')} required={isCostAllocationMandatory} />
      {loading ? (
        <div style={{ padding: '8px', color: colors.placeholderColor, fontSize: '14px' }}>
          {i18n('loading') || 'Loading...'}
        </div>
      ) : (
        <>
          <ChipGroup
            options={costAllocationOptions}
            selectedValues={formData.costAllocation}
            onChange={(value) => {
              updateField('costAllocation', value);

              if (value && error) {
                clearError('costAllocation');
              }

              if (value === 'other') {
                updateField('costAllocationGroup', null);
                // Picking "other" deselects the cost center, so its default has to go with
                // it. Left behind, it would look like a value the customer typed once a
                // group is chosen from the search below (TUL-5597).
                applyReferenceDefaults({
                  group: null,
                  previousGroup: formData.costAllocationGroup,
                  currentValues: {
                    additionalInvoicingInfo: formData.additionalInvoicingInfo,
                    externalOrderId: formData.externalOrderId,
                  },
                });
              } else {
                const matchedGroup = userGroups.find(g => g.id === value);
                if (matchedGroup) {
                  updateField('costAllocationGroup', {
                    id: matchedGroup.id,
                    name: matchedGroup.publicName || matchedGroup.name,
                    invoiceRecipientRef: matchedGroup.invoiceRecipientRef,
                    invoiceRefDefault: matchedGroup.invoiceRefDefault,
                    externalOrderIdDefault: matchedGroup.externalOrderIdDefault
                  });
                  applyReferenceDefaults({
                    group: matchedGroup,
                    previousGroup: formData.costAllocationGroup,
                    currentValues: {
                      additionalInvoicingInfo: formData.additionalInvoicingInfo,
                      externalOrderId: formData.externalOrderId,
                    },
                  });
                }
              }
            }}
          />
          {formData.costAllocation === 'other' && (
            <SearchContainer ref={searchContainerRef}>
              <UsergroupSelect
                containerRef={costAllocationContainerRef}
                scrollContainerRef={searchContainerRef}
                selected={formData.costAllocationGroup}
                onChange={(group) => {
                  // The full group comes from the search-result cache; a cache miss is treated
                  // as "no defaults", which clears a stale prefill instead of leaving the
                  // previous cost center's reference on screen.
                  const searchedGroup = searchedGroupsRef.current.get(group.id);
                  updateField('costAllocationGroup', {
                    ...group,
                    invoiceRefDefault: searchedGroup?.invoiceRefDefault,
                    externalOrderIdDefault: searchedGroup?.externalOrderIdDefault,
                  });
                  applyReferenceDefaults({
                    group: searchedGroup,
                    previousGroup: formData.costAllocationGroup,
                    currentValues: {
                      additionalInvoicingInfo: formData.additionalInvoicingInfo,
                      externalOrderId: formData.externalOrderId,
                    },
                  });
                  if (error) {
                    clearError('costAllocation');
                  }
                }}
                getSelectableItems={searchUserGroups}
              />
            </SearchContainer>
          )}
        </>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </Section>
  );
};

CostAllocation.propTypes = {
  formData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  isCostAllocationMandatory: PropTypes.bool,
  error: PropTypes.string,
  clearError: PropTypes.func.isRequired,
};

export default CostAllocation;
